import { supabase } from "@/integrations/supabase/client";
import { normalizeMerchantName } from "./merchantNormalizer";

/**
 * Determines if a receipt is a business expense based on AI assessment
 * SINGLE SOURCE OF TRUTH for business expense logic
 */
export function isBusinessExpense(parsedJson: any): boolean {
  return parsedJson?.business_expense_assessment?.overall_status !== 'no';
}

/**
 * Updates merchant learning data when a user manually corrects item categories
 * This improves auto-categorization accuracy over time
 */
export async function updateMerchantLearning(
  merchantName: string,
  updatedCategories: string[]
) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Normalize merchant name for consistent matching
    const normalizedMerchantName = normalizeMerchantName(merchantName);

    // Get existing merchant data
    const { data: merchant } = await supabase
      .from('merchants')
      .select('common_categories')
      .eq('normalized_name', normalizedMerchantName)
      .eq('user_id', user.id)
      .single();

    if (merchant) {
      // Merge new categories with existing ones, keeping unique values
      const existingCategories = Array.isArray(merchant.common_categories) 
        ? merchant.common_categories 
        : [];
      
      const mergedCategories = [...new Set([...existingCategories, ...updatedCategories])];

      // Update merchant record
      await supabase
        .from('merchants')
        .update({ 
          common_categories: mergedCategories,
          last_seen: new Date().toISOString()
        })
        .eq('normalized_name', normalizedMerchantName)
        .eq('user_id', user.id);
    }
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.error("Error updating merchant learning:", error);
    }
  }
}

/**
 * Updates monthly financial summary when a receipt is added, edited, or deleted
 * ATOMIC operation to prevent race conditions
 */
export async function updateFinancialSummary(
  receiptDate: string,
  receiptTotal: number,
  isBusinessExpense: boolean,
  taxSavings: number,
  operation: 'add' | 'subtract'
) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const monthYear = new Date(receiptDate).toISOString().substring(0, 7);
    const multiplier = operation === 'add' ? 1 : -1;

    const { data: existingSummary } = await supabase
      .from('summaries')
      .select('*')
      .eq('month_year', monthYear)
      .eq('user_id', user.id) // Security: user_id filter
      .maybeSingle(); // Use maybeSingle to avoid error if not found

    const businessExpense = (existingSummary?.monthly_business_expenses || 0) + 
      (isBusinessExpense ? receiptTotal * multiplier : 0);
    const personalExpense = (existingSummary?.monthly_personal_expenses || 0) + 
      (!isBusinessExpense ? receiptTotal * multiplier : 0);
    const savings = (existingSummary?.monthly_tax_savings || 0) + (taxSavings * multiplier);
    const count = (existingSummary?.monthly_receipt_count || 0) + (1 * multiplier);

    await supabase
      .from('summaries')
      .upsert({
        user_id: user.id,
        month_year: monthYear,
        monthly_business_expenses: Math.max(0, businessExpense),
        monthly_personal_expenses: Math.max(0, personalExpense),
        monthly_tax_savings: Math.max(0, savings),
        monthly_receipt_count: Math.max(0, count)
      }, {
        onConflict: 'user_id,month_year'
      });
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.error("Error updating financial summary:", error);
    }
    throw error; // Re-throw to allow caller to handle
  }
}

/**
 * Checks for duplicate receipts before saving
 */
export async function checkDuplicateReceipt(
  merchantName: string,
  date: string,
  total: number
): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    // Normalize merchant name for duplicate detection
    const normalizedMerchantName = normalizeMerchantName(merchantName);

    const { data } = await supabase
      .from('receipts')
      .select('id')
      .eq('merchant_name', normalizedMerchantName)
      .eq('date', date)
      .eq('total', total)
      .eq('user_id', user.id)
      .limit(1);

    return (data && data.length > 0) || false;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.error("Error checking duplicate:", error);
    }
    return false;
  }
}

/**
 * Retrieves merchant's common categories for auto-fill
 */
export async function getMerchantCategories(merchantName: string): Promise<string[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    // Normalize merchant name for lookup
    const normalizedMerchantName = normalizeMerchantName(merchantName);

    const { data } = await supabase
      .from('merchants')
      .select('common_categories')
      .eq('normalized_name', normalizedMerchantName)
      .eq('user_id', user.id)
      .single();

    if (!data?.common_categories) return [];
    
    // Parse JSONB to string array
    const categories = data.common_categories;
    return Array.isArray(categories) ? categories.filter((c): c is string => typeof c === 'string') : [];
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.error("Error getting merchant categories:", error);
    }
    return [];
  }
}

/**
 * Deletes a receipt and updates the monthly financial summaries accordingly
 */
export async function deleteReceiptAndSyncSummary(receiptId: string) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("User not authenticated");

    // Get receipt details before deletion
    const { data: receipt, error: fetchError } = await supabase
      .from('receipts')
      .select('date, total, parsed_json, tax_savings_estimate, merchant_name')
      .eq('id', receiptId)
      .single();

    if (fetchError) throw fetchError;
    if (!receipt) throw new Error("Receipt not found");

    // Determine if it was a business expense using helper
    const parsedJson = receipt.parsed_json as any;
    const isBusiness = isBusinessExpense(parsedJson);

    // Delete the receipt
    const { error: deleteError } = await supabase
      .from('receipts')
      .delete()
      .eq('id', receiptId)
      .eq('user_id', user.id); // Security: user_id filter

    if (deleteError) throw deleteError;

    // Decrement merchant counter
    if (receipt.merchant_name) {
      await supabase.rpc('decrement_merchant_counter', {
        p_merchant_name: receipt.merchant_name,
        p_user_id: user.id
      });
    }

    // Update financial summary by subtracting the deleted receipt
    await updateFinancialSummary(
      receipt.date,
      receipt.total || 0,
      isBusiness,
      receipt.tax_savings_estimate || 0,
      'subtract'
    );
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.error("Error deleting receipt and syncing summary:", error);
    }
    throw error;
  }
}
